"use client"

import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import {
  BOOKING_FILTERS,
  RANGE_OPTIONS,
  type UseTravelDeskResult,
} from "@/components/travel-desk/use-travel-desk"

/**
 * Travel Desk filters — the page header's action slot.
 *
 * Three controls, each answering a question the coordinator actually asks:
 * how far ahead am I looking, who still needs their visit booked, and is any
 * airport run still sitting in the pool. Anything else belongs on the trip
 * itself, not on a roster.
 */
export function TravelDeskFilters({ board }: { board: UseTravelDeskResult }) {
  const t = useTranslations()

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={String(board.range)}
        onValueChange={(value) => board.setRange(Number(value))}
      >
        <SelectTrigger className="h-8 w-[136px] text-[12.5px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RANGE_OPTIONS.map((days) => (
            <SelectItem key={days} value={String(days)}>
              {t("travelDesk.filters.nextDays", { days })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Segmented rather than a second select: three mutually exclusive
          options the coordinator flips between constantly, and a dropdown would
          hide the current one behind a click. */}
      <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
        {BOOKING_FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => board.setBooking(filter.key)}
            className={cn(
              "rounded-md px-2.5 py-1 text-[11.5px] font-medium transition-colors",
              board.booking === filter.key
                ? "bg-muted text-foreground"
                : "text-text-dim hover:text-foreground",
            )}
          >
            {t(filter.labelKey)}
          </button>
        ))}
      </div>

      <Button
        variant={board.unclaimedOnly ? "default" : "outline"}
        size="sm"
        className="h-8 text-[11.5px]"
        onClick={() => board.setUnclaimedOnly(!board.unclaimedOnly)}
      >
        {t("travelDesk.filters.unclaimedOnly")}
      </Button>
    </div>
  )
}
